import {
  Badge,
  Button,
  Card,
  CardContent,
  ConfirmDialog,
  FormDialog,
  LoadingState,
  PageLayout,
  Progress,
  SectionCard,
  SegmentedControl,
  StatusBadge,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@repo/ui'
import { createFileRoute, useNavigate, useParams } from '@tanstack/react-router'
import { ArrowLeftIcon, DownloadIcon, SmartphoneIcon } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'

import { CampaignAgentSelect } from '@/components/campaigns/CampaignAgentSelect'
import { useAuth } from '@/hooks/use-auth'
import {
  useCampaign,
  useCampaignMetrics,
  useCampaignRecipients,
  useCancelCampaign,
  useExportCampaignRecipients,
  useLaunchCampaign,
  usePauseCampaign,
  useResumeCampaign,
  useUpdateCampaign,
} from '@/hooks/use-campaigns'
import { useChannels } from '@/hooks/use-channels'
import { CAMPAIGN_STATUS_TONE } from '@/lib/status-tones'
import {
  CAMPAIGN_SKIP_REASON_LABEL,
  type CampaignRecipientStatus,
  type CampaignStatus,
} from '@/types/campaigns'

export const Route = createFileRoute('/_app/campanhas/$campaignId')({
  component: CampaignDetailPage,
})

const STATUS_LABELS: Record<CampaignStatus, string> = {
  DRAFT: 'Rascunho',
  SCHEDULED: 'Agendada',
  RUNNING: 'Enviando',
  PAUSED: 'Pausada',
  COMPLETED: 'Concluída',
  CANCELLED: 'Cancelada',
  FAILED: 'Falhou',
}

const RECIPIENT_STATUS_LABELS: Record<CampaignRecipientStatus, string> = {
  PENDING: 'Pendente',
  SKIPPED: 'Pulado',
  QUEUED: 'Na fila',
  SENT: 'Enviado',
  DELIVERED: 'Entregue',
  READ: 'Lido',
  REPLIED: 'Respondeu',
  FAILED: 'Falhou',
}

const RECIPIENT_FILTERS: { value: string; label: string }[] = [
  { value: 'all', label: 'Todos' },
  { value: 'SENT', label: 'Enviados' },
  { value: 'FAILED', label: 'Falhas' },
  { value: 'SKIPPED', label: 'Pulados' },
  { value: 'PENDING', label: 'Pendentes' },
]

function CampaignDetailPage() {
  const { currentOrgId } = useAuth()
  const navigate = useNavigate()
  const { campaignId } = useParams({ from: '/_app/campanhas/$campaignId' })
  const [recipientFilter, setRecipientFilter] = useState('all')
  const [agentDialogOpen, setAgentDialogOpen] = useState(false)
  const [selectedAgentId, setSelectedAgentId] = useState<string | null>(null)
  const [pendingAction, setPendingAction] = useState<
    'launch' | 'pause' | 'resume' | 'cancel' | null
  >(null)

  const { data: campaign, isLoading } = useCampaign(currentOrgId, campaignId)
  const { data: channels = [] } = useChannels(currentOrgId)
  const { data: metrics } = useCampaignMetrics(currentOrgId, campaignId)
  const { data: recipients } = useCampaignRecipients(
    currentOrgId,
    campaignId,
    recipientFilter
  )

  const launch = useLaunchCampaign(currentOrgId)
  const pause = usePauseCampaign(currentOrgId)
  const resume = useResumeCampaign(currentOrgId)
  const cancel = useCancelCampaign(currentOrgId)
  const exportCsv = useExportCampaignRecipients(currentOrgId)
  const updateCampaign = useUpdateCampaign(currentOrgId)

  if (isLoading || !campaign) {
    return (
      <PageLayout title="Campanha">
        <LoadingState />
      </PageLayout>
    )
  }

  const channelName = channels.find((c) => c.id === campaign.channelId)?.name
  const counters = metrics?.counters
  const total = metrics?.totalRecipients ?? campaign.totalRecipients
  const done =
    (counters?.sent ?? campaign.sent) +
    (counters?.failed ?? campaign.failed) +
    (counters?.skipped ?? campaign.skipped)
  const progress = total > 0 ? Math.round((done / total) * 100) : 0

  type ActionKey = 'launch' | 'pause' | 'resume' | 'cancel'
  const ACTIONS: Record<
    ActionKey,
    {
      label: string
      title: string
      description: string
      confirmLabel: string
      mutate: (id: string) => Promise<unknown>
    }
  > = {
    launch: {
      label: 'Enviar agora',
      title: 'Enviar a campanha agora?',
      description:
        'Vamos resolver o público (removendo duplicados, contatos sem opt-in e sem WhatsApp) e começar a enviar imediatamente, respeitando o ritmo configurado.',
      confirmLabel: 'Enviar agora',
      mutate: launch.mutateAsync,
    },
    pause: {
      label: 'Pausar',
      title: 'Pausar a campanha?',
      description:
        'O envio é interrompido e nenhum novo destinatário recebe até você retomar. Mensagens já na fila de saída podem concluir.',
      confirmLabel: 'Pausar',
      mutate: pause.mutateAsync,
    },
    resume: {
      label: 'Retomar',
      title: 'Retomar a campanha?',
      description: 'O envio volta a processar os destinatários pendentes.',
      confirmLabel: 'Retomar',
      mutate: resume.mutateAsync,
    },
    cancel: {
      label: 'Cancelar',
      title: 'Cancelar a campanha?',
      description:
        'A campanha é encerrada em definitivo e os destinatários pendentes não serão enviados. Esta ação não pode ser desfeita.',
      confirmLabel: 'Cancelar campanha',
      mutate: cancel.mutateAsync,
    },
  }

  function confirmAction() {
    if (!pendingAction) return
    const action = ACTIONS[pendingAction]
    setPendingAction(null)
    action
      .mutate(campaignId)
      .then(() => toast.success('Campanha atualizada'))
      .catch((err: { response?: { data?: { message?: string } } }) =>
        toast.error(err?.response?.data?.message ?? 'Falha ao atualizar')
      )
  }

  const isPending =
    launch.isPending || pause.isPending || resume.isPending || cancel.isPending

  async function saveAgent() {
    try {
      await updateCampaign.mutateAsync({
        id: campaignId,
        data: { aiAgentId: selectedAgentId },
      })
      setAgentDialogOpen(false)
      toast.success('Agente da campanha atualizado')
    } catch (error) {
      const message = (error as { response?: { data?: { message?: string } } })
        ?.response?.data?.message
      toast.error(message ?? 'Falha ao atualizar o agente')
    }
  }

  return (
    <PageLayout
      contentClassName="bg-muted/40 pb-24"
      title={campaign.name}
      description={
        <span className="flex items-center gap-1">
          <SmartphoneIcon className="size-3.5" /> {channelName ?? 'Canal'}
        </span>
      }
      back={
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={() => void navigate({ to: '/campanhas' })}
          title="Voltar para Campanhas"
        >
          <ArrowLeftIcon className="h-4 w-4" />
        </Button>
      }
      meta={
        <>
          <StatusBadge tone={CAMPAIGN_STATUS_TONE[campaign.status]}>
            {STATUS_LABELS[campaign.status]}
          </StatusBadge>
          <Badge variant="outline" className="text-2xs">
            {campaign.contentMode === 'TEMPLATE' ? 'Template' : 'Texto livre'}
          </Badge>
        </>
      }
      actions={
        <>
          <Button
            variant="outline"
            onClick={() => {
              exportCsv
                .mutateAsync(campaignId)
                .catch(() => toast.error('Falha ao exportar'))
            }}
            disabled={exportCsv.isPending}
          >
            <DownloadIcon className="h-4 w-4" />
            Exportar CSV
          </Button>
          {(campaign.status === 'DRAFT' || campaign.status === 'SCHEDULED') && (
            <Button
              onClick={() => setPendingAction('launch')}
              disabled={isPending}
            >
              {ACTIONS.launch.label}
            </Button>
          )}
          {(campaign.status === 'RUNNING' ||
            campaign.status === 'SCHEDULED') && (
            <Button
              variant="outline"
              onClick={() => setPendingAction('pause')}
              disabled={isPending}
            >
              {ACTIONS.pause.label}
            </Button>
          )}
          {campaign.status === 'PAUSED' && (
            <Button
              onClick={() => setPendingAction('resume')}
              disabled={isPending}
            >
              {ACTIONS.resume.label}
            </Button>
          )}
          {['DRAFT', 'SCHEDULED', 'RUNNING', 'PAUSED'].includes(
            campaign.status
          ) && (
            <Button
              variant="destructive"
              onClick={() => setPendingAction('cancel')}
              disabled={isPending}
            >
              {ACTIONS.cancel.label}
            </Button>
          )}
        </>
      }
    >
      <ConfirmDialog
        open={!!pendingAction}
        onOpenChange={(open) => !open && setPendingAction(null)}
        title={pendingAction ? ACTIONS[pendingAction].title : ''}
        description={pendingAction ? ACTIONS[pendingAction].description : ''}
        confirmLabel={
          pendingAction ? ACTIONS[pendingAction].confirmLabel : 'Confirmar'
        }
        cancelLabel="Voltar"
        destructive={pendingAction === 'cancel'}
        onConfirm={confirmAction}
      />

      <SectionCard
        title="Continuidade do atendimento"
        description="Agente de IA responsável pelas respostas após o disparo."
        actions={
          campaign.status === 'DRAFT' ? (
            <Button
              variant="outline"
              onClick={() => {
                setSelectedAgentId(campaign.aiAgentId)
                setAgentDialogOpen(true)
              }}
              disabled={isPending || updateCampaign.isPending}
            >
              Alterar agente
            </Button>
          ) : undefined
        }
      >
        <p className="text-sm font-medium">
          {campaign.aiAgent?.name ?? 'Sem atribuição automática'}
          {campaign.aiAgent && !campaign.aiAgent.isActive ? ' (inativo)' : ''}
        </p>
        <p className="text-muted-foreground mt-1 text-xs">
          Atendimentos com um responsável humano são preservados.
        </p>
      </SectionCard>

      <FormDialog
        title="Agente da campanha"
        open={agentDialogOpen}
        onOpenChange={setAgentDialogOpen}
        submitLabel="Salvar agente"
        pending={updateCampaign.isPending}
        onSubmit={() => void saveAgent()}
      >
        <CampaignAgentSelect
          orgId={currentOrgId}
          value={selectedAgentId}
          onValueChange={setSelectedAgentId}
          disabled={updateCampaign.isPending}
        />
      </FormDialog>

      {campaign.status === 'PAUSED' && metrics?.pauseReason ? (
        <div className="border-warning/30 bg-warning-soft text-warning-soft-foreground rounded-lg border px-4 py-3 text-sm">
          {pauseReasonLabel(metrics.pauseReason)}
        </div>
      ) : null}

      <Card>
        <CardContent className="space-y-3 py-4">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Progresso</span>
            <span>
              {done} / {total} ({progress}%)
            </span>
          </div>
          <Progress value={progress} />
          <div className="grid grid-cols-3 gap-3 pt-2 sm:grid-cols-6">
            <Kpi label="Total" value={total} />
            <Kpi label="Enviadas" value={counters?.sent ?? campaign.sent} />
            <Kpi
              label="Entregues"
              value={counters?.delivered ?? campaign.delivered}
            />
            <Kpi label="Lidas" value={counters?.read ?? campaign.read} />
            <Kpi
              label="Respostas"
              value={counters?.replied ?? campaign.replied}
            />
            <Kpi label="Falhas" value={counters?.failed ?? campaign.failed} />
          </div>
          {metrics?.rates ? (
            <div className="grid grid-cols-2 gap-3 border-t pt-3 sm:grid-cols-4">
              <Kpi label="Taxa entrega" value={pct(metrics.rates.delivery)} />
              <Kpi label="Taxa leitura" value={pct(metrics.rates.read)} />
              <Kpi label="Taxa resposta" value={pct(metrics.rates.reply)} />
              <Kpi label="Taxa falha" value={pct(metrics.rates.failure)} />
            </div>
          ) : null}
        </CardContent>
      </Card>

      {metrics?.timeline && metrics.timeline.length > 0 ? (
        <Card>
          <CardContent className="space-y-2 py-4">
            <span className="text-muted-foreground text-sm">
              Envios por dia
            </span>
            <SendTimeline data={metrics.timeline} />
          </CardContent>
        </Card>
      ) : null}

      <div className="space-y-3">
        <SegmentedControl
          value={recipientFilter}
          onChange={setRecipientFilter}
          options={RECIPIENT_FILTERS}
        />

        <div className="bg-card overflow-hidden rounded-lg border shadow-sm">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="pl-6">Contato</TableHead>
                <TableHead>Telefone</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="pr-6">Detalhe</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(recipients?.data ?? []).map((recipient) => (
                <TableRow key={recipient.id}>
                  <TableCell className="pl-6 font-medium">
                    {recipient.contact?.name ?? '—'}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {recipient.contact?.phone ?? '—'}
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className="text-2xs">
                      {RECIPIENT_STATUS_LABELS[recipient.status]}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-muted-foreground max-w-xs truncate pr-6 text-xs">
                    {recipient.skipReason
                      ? (CAMPAIGN_SKIP_REASON_LABEL[recipient.skipReason] ??
                        recipient.skipReason)
                      : (recipient.error ?? '')}
                  </TableCell>
                </TableRow>
              ))}
              {(recipients?.data ?? []).length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={4}
                    className="text-muted-foreground py-8 text-center text-sm"
                  >
                    Nenhum destinatário neste filtro.
                  </TableCell>
                </TableRow>
              ) : null}
            </TableBody>
          </Table>
        </div>
      </div>
    </PageLayout>
  )
}

function Kpi({ label, value }: { label: string; value: number | string }) {
  return (
    <div className="rounded-md border p-2 text-center">
      <div className="text-lg font-semibold">{value}</div>
      <div className="text-muted-foreground text-xs">{label}</div>
    </div>
  )
}

/** Taxa 0–1 → percentual legível. */
function pct(rate: number): string {
  return `${Math.round(rate * 100)}%`
}

function pauseReasonLabel(reason: string): string {
  if (reason === 'BILLING_LIMIT') {
    return 'Pausada: o limite de mensagens do seu plano foi atingido. Faça upgrade e retome a campanha.'
  }
  if (reason === 'META_RATE_LIMIT') {
    return 'Pausada automaticamente: limite de envio do WhatsApp (META) atingido. A campanha retoma sozinha em alguns minutos.'
  }
  if (reason === 'AGENT_UNAVAILABLE') {
    return 'Pausada: o agente de IA selecionado está indisponível. Reative o agente e retome a campanha.'
  }
  return `Pausada: ${reason}`
}

/** Mini-gráfico de barras: envios por dia. */
function SendTimeline({
  data,
}: {
  data: Array<{ date: string; count: number }>
}) {
  const max = Math.max(...data.map((d) => d.count), 1)
  return (
    <div className="flex h-28 items-end gap-1">
      {data.map((d) => (
        <div
          key={d.date}
          className="flex flex-1 flex-col items-center gap-1"
          title={`${d.date}: ${d.count}`}
        >
          <div
            className="bg-primary/70 w-full rounded-t"
            style={{ height: `${Math.round((d.count / max) * 100)}%` }}
          />
          <span className="text-muted-foreground text-2xs">
            {d.date.slice(5)}
          </span>
        </div>
      ))}
    </div>
  )
}
